import { computed, ref, shallowRef, type ComputedRef } from 'vue'

/** Chromium's install offer; not in lib.dom. */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function isInstallPrompt(event: Event): event is InstallPromptEvent {
  return 'prompt' in event && 'userChoice' in event
}

const offer = shallowRef<InstallPromptEvent | null>(null)
const installed = ref(false)

/**
 * Chromium makes its install offer once, early in the page's life, so the
 * studio catches it at startup and keeps it until Settings asks for it.
 */
export function listenForWebAppInstall(target: EventTarget = window): void {
  target.addEventListener('beforeinstallprompt', (event) => {
    if (!isInstallPrompt(event)) return
    event.preventDefault()
    offer.value = event
  })
  target.addEventListener('appinstalled', () => {
    offer.value = null
    installed.value = true
  })
}

/**
 * How this browser installs the studio: its own prompt (Chrome, Edge), the
 * File menu (Safari on a Mac, which never makes an offer), or not at all
 * (already installed, the S3T app's window, Firefox, phones).
 */
export type WebAppInstallMode = 'prompt' | 'safari' | 'none'

export function webAppInstallMode(browser: {
  standalone: boolean
  installed: boolean
  offered: boolean
  userAgent: string
  maxTouchPoints: number
}): WebAppInstallMode {
  if (browser.standalone || browser.installed) return 'none'
  if (browser.offered) return 'prompt'
  const ua = browser.userAgent
  // An iPad reports a Mac too; only a real Mac has File › Add to Dock.
  const macSafari =
    ua.includes('Macintosh') &&
    /Version\/\d+[\d.]* Safari\//.test(ua) &&
    !/Chrome|Chromium|CriOS|Edg|Firefox|FxiOS/.test(ua) &&
    browser.maxTouchPoints <= 1
  return macSafari ? 'safari' : 'none'
}

export function useWebAppInstall(): { mode: ComputedRef<WebAppInstallMode>; install: () => Promise<boolean> } {
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches ?? false
  const mode = computed(() =>
    webAppInstallMode({
      standalone,
      installed: installed.value,
      offered: offer.value !== null,
      userAgent: navigator.userAgent,
      maxTouchPoints: navigator.maxTouchPoints ?? 0,
    }),
  )
  async function install(): Promise<boolean> {
    const event = offer.value
    if (!event) return false
    // An offer can be shown once; Chromium makes a new one if this is dismissed.
    offer.value = null
    await event.prompt()
    return (await event.userChoice).outcome === 'accepted'
  }
  return { mode, install }
}
